import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { retryWhen, concatMap, delay } from 'rxjs/operators';
import { throwError, of, Observable } from 'rxjs';
import { Settings } from '@app/app.settings';
import { LoggerService } from '@shared/services/logger.service';

export interface RetryRequestOptions {
  maximumRetries: number;
  retryDelay: number;
  maxDelay: number;
}

@Injectable()
export class RetryHttpErrorsInterceptor implements HttpInterceptor {
  private retryRequestOptions: RetryRequestOptions = Settings.retryRequestOptions;

  private readonly logger = inject(LoggerService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(this.retryPipe());
  }

  private retryPipe<T>() {
    return retryWhen<T>((error) =>
      error.pipe(
        concatMap((error: HttpErrorResponse, attempt) => {
          if (attempt <= this.retryRequestOptions.maximumRetries && this.statusMustBeRetried(error)) {
            return this.doRetry(attempt, error);
          }
          return throwError(() => error);
        })
      )
    );
  }

  private doRetry(attempt: number, error: HttpErrorResponse) {
    const nextDelay = this.backoff(attempt);
    this.logger.info(`Retry failed request attempt ${attempt},  retry in ${nextDelay}ms`, error);
    return of(error).pipe(delay(nextDelay));
  }

  private statusMustBeRetried(error: HttpErrorResponse) {
    return error.status == 0 || error.status >= 500;
  }

  private backoff(attempt: number) {
    const exponential = Math.pow(2, attempt + 1) * this.retryRequestOptions.retryDelay;
    const delay = Math.min(exponential, this.retryRequestOptions.maxDelay);
    return Math.max(Math.floor(Math.random() * delay), this.retryRequestOptions.retryDelay);
  }
}
